/**
 * PathFinder.ts
 * BFS pathfinding through the octile grid, respecting wall states.
 * Uses corrected geometry: connectors at diagonal intersections.
 */

import type { GridCell, GridPos } from '../types/OctileTypes.ts'
import { PASSABLE_WALLS } from '../types/OctileTypes.ts'
import {
    getNeighbors, gridKey, parseGridKey,
    getOctWallToward, getConnWallToward
} from './GridAddressing.ts'
import { getCell } from './OctileGrid.ts'

// ─── Traversal Check ────────────────────────────────────────

/**
 * Check if movement is possible from one cell to an adjacent cell.
 * Both cells must be active and the connecting wall must be passable.
 */
export function canTraverse(
    grid: GridCell[][],
    from: GridPos,
    to: GridPos
): boolean {
    const fromCell = getCell(grid, from.row, from.col)
    const toCell = getCell(grid, to.row, to.col)

    if (!fromCell?.active || !toCell?.active) return false

    // Get the wall on the 'from' side that faces 'to'
    let wallIndex: number
    if (fromCell.kind === 'octagon') {
        wallIndex = getOctWallToward(from.row, from.col, to.row, to.col)
    } else {
        wallIndex = getConnWallToward(from.row, from.col, to.row, to.col)
    }

    if (wallIndex === -1) return false

    const wallState = fromCell.walls[wallIndex]
    return (PASSABLE_WALLS as readonly string[]).includes(wallState)
}

// ─── BFS Pathfinding ────────────────────────────────────────

/**
 * Find the shortest path between two cells using BFS.
 */
export function findPath(
    grid: GridCell[][],
    from: GridPos,
    to: GridPos
): GridPos[] | null {
    const gridWidth = grid[0]?.length ?? 0
    const gridHeight = grid.length

    const visited = new Set<string>()
    const parent = new Map<string, string>()
    const queue: GridPos[] = [from]

    visited.add(gridKey(from.row, from.col))

    while (queue.length > 0) {
        const current = queue.shift()!

        if (current.row === to.row && current.col === to.col) {
            const path: GridPos[] = []
            let key = gridKey(to.row, to.col)
            while (key) {
                path.unshift(parseGridKey(key))
                key = parent.get(key)!
            }
            return path
        }

        const neighbors = getNeighbors(current.row, current.col, gridWidth, gridHeight)
        for (const neighbor of neighbors) {
            const nKey = gridKey(neighbor.row, neighbor.col)
            if (visited.has(nKey)) continue
            if (!canTraverse(grid, current, neighbor)) continue

            visited.add(nKey)
            parent.set(nKey, gridKey(current.row, current.col))
            queue.push(neighbor)
        }
    }

    return null
}

// ─── Flood Fill ─────────────────────────────────────────────

/**
 * Find all cells reachable from a starting position through open walls.
 */
export function floodFillReachable(
    grid: GridCell[][],
    start: GridPos
): Set<string> {
    const gridWidth = grid[0]?.length ?? 0
    const gridHeight = grid.length
    const reachable = new Set<string>()
    const queue: GridPos[] = [start]

    const startCell = getCell(grid, start.row, start.col)
    if (!startCell?.active) return reachable

    reachable.add(gridKey(start.row, start.col))

    while (queue.length > 0) {
        const current = queue.shift()!
        const neighbors = getNeighbors(current.row, current.col, gridWidth, gridHeight)

        for (const neighbor of neighbors) {
            const nKey = gridKey(neighbor.row, neighbor.col)
            if (reachable.has(nKey)) continue
            if (!canTraverse(grid, current, neighbor)) continue

            reachable.add(nKey)
            queue.push(neighbor)
        }
    }

    return reachable
}

/**
 * Check if all active cells are reachable from the entrance.
 */
export function isFullyConnected(
    grid: GridCell[][],
    entrance: GridPos
): boolean {
    const reachable = floodFillReachable(grid, entrance)

    for (let row = 0; row < grid.length; row++) {
        for (let col = 0; col < grid[row].length; col++) {
            const cell = grid[row][col]
            if (cell?.active && !reachable.has(gridKey(row, col))) {
                return false
            }
        }
    }

    return true
}

/**
 * Get shortest distance (in cells) between two positions.
 */
export function getDistance(
    grid: GridCell[][],
    from: GridPos,
    to: GridPos
): number {
    const path = findPath(grid, from, to)
    return path ? path.length - 1 : -1
}
